import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { describeError } from "../api/errors";
import { takeTitleFocus } from "../app/focus";
import { thumbnailUrl } from "../lib/youtube";
import { RibatanNote, SkyBits, type RibatanPose } from "./stage";
import { Icon, type IconName } from "./Icon";

/* ---------------------------------------------------------------- 小さな札（参照元の .pill） */

export type PillTone = "calm" | "info" | "warn" | "danger" | "draft" | "petal";

export function Pill({ tone = "info", children, icon }: { tone?: PillTone; children: ReactNode; icon?: IconName }) {
  return (
    <span className={`pill pill--${tone}`}>
      {icon && <Icon name={icon} className="pill__icon" />}
      {children}
    </span>
  );
}

/* ---------------------------------------------------------------- サムネイル（読めなければ色の下地と頭文字） */

export function Thumb({ videoId, title, size = "m" }: { videoId: string; title: string; size?: "s" | "m" | "l" }) {
  const [fallbackId, setFallbackId] = useState<string | null>(null);
  const [failedId, setFailedId] = useState<string | null>(null);
  const large = size === "l" && fallbackId !== videoId;
  const onUnavailable = () => large ? setFallbackId(videoId) : setFailedId(videoId);
  // 括弧や記号は飛ばして、最初の文字・数字を出す（「【歌枠】…」なら「歌」）
  const initial =
    Array.from(title.replace(/【[^】]*】/g, "")).find((c) => /[\p{L}\p{N}]/u.test(c)) ??
    Array.from(title).find((c) => /[\p{L}\p{N}]/u.test(c)) ??
    "♪";
  return (
    <span className={`thumb thumb--${size}`} aria-hidden="true">
      <span className="thumb__fallback">{initial}</span>
      {failedId !== videoId && (
        <img
          key={thumbnailUrl(videoId, large)}
          src={thumbnailUrl(videoId, large)}
          alt=""
          width={large ? 1280 : 320}
          height={large ? 720 : 180}
          loading={size === "l" ? "eager" : "lazy"}
          decoding="async"
          referrerPolicy="no-referrer"
          onError={onUnavailable}
          onLoad={(e) => {
            // YouTube は無い動画にも灰色の小さな絵を返すので、それは使わない
            if (e.currentTarget.naturalWidth <= 120) onUnavailable();
          }}
        />
      )}
    </span>
  );
}

/* ---------------------------------------------------------------- 画面の見出し（文字が1つずつ跳ねて出る。参照元の page-hero__char） */

/**
 * 画面の見出し。ほろりばの広場の絵（昼・夜）を敷き、見出しと説明を白いもやの札にのせる（参照元の page-hero）。
 * stage のときは絵を高くとり、すぐ下の部品（いま流れている曲など）が絵の上に重なる
 */
export function PageHeader({
  title,
  lead,
  children,
  stage = false,
}: {
  title: string;
  lead?: ReactNode;
  children?: ReactNode;
  stage?: boolean;
}) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    document.title = `${title} | ほろりばレコード`;
  }, [title]);
  // 画面を移ってきたとき、見出しが（読み込みのあとで）出たら、ここへフォーカスを移す
  useEffect(() => takeTitleFocus(ref.current), []);
  return (
    <header className={`page-hero${stage ? " page-hero--stage" : ""}`}>
      <SkyBits count={stage ? 9 : 6} seed={title.length} />
      <div className="page-hero__inner">
        <h1 className="page-hero__title" tabIndex={-1} data-page-title ref={ref}>
          <span className="visually-hidden">{title}</span>
          <span className="page-hero__chars" aria-hidden="true">
            {Array.from(title).map((ch, i) => (
              <span key={i} className="page-hero__char" data-ch={ch} style={{ "--i": i } as CSSProperties} />
            ))}
          </span>
        </h1>
        {lead && <p className="page-hero__lead">{lead}</p>}
        {children && <div className="page-hero__extra">{children}</div>}
      </div>
    </header>
  );
}

/* ---------------------------------------------------------------- 状態の表示 */

export function EmptyState({
  title,
  children,
  icon = "wave",
  ribatan,
}: {
  title: string;
  children?: ReactNode;
  icon?: IconName;
  /** 画面の中心になる「空っぽ」には、りばたんを出す（ひとことと姿勢） */
  ribatan?: { pose: RibatanPose; say: string };
}) {
  if (ribatan) {
    return (
      <div className="empty empty--ribatan">
        <RibatanNote pose={ribatan.pose} say={ribatan.say} title={title} size="s">
          {children}
        </RibatanNote>
      </div>
    );
  }
  return (
    <div className="empty">
      <Icon name={icon} className="empty__icon" />
      <p className="empty__title">{title}</p>
      {children && <div className="empty__body">{children}</div>}
    </div>
  );
}

export function Loading({ label = "読み込み中…", rows = 3 }: { label?: string; rows?: number }) {
  return (
    <div className="loading" role="status" aria-live="polite">
      <span className="visually-hidden">{label}</span>
      {Array.from({ length: rows }, (_, i) => (
        <span key={i} className="skeleton" aria-hidden="true" />
      ))}
    </div>
  );
}

export function NotConnected({ what }: { what: string }) {
  return (
    <div className="notice notice--draft" role="note">
      <Icon name="link" className="notice__icon" />
      <div>
        <p className="notice__title">{what}は、まだAPIに接続されていません</p>
        <p>API契約が届いたら接続します。いまは表示も操作もできません（成功したようには見せません）。</p>
      </div>
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="load-error" role="alert">
      <RibatanNote pose="lost" say="あれれ…？" title="読み込めませんでした" size="s">
        <p>{describeError(error)}</p>
        {onRetry && (
          <button type="button" className="btn btn-secondary btn-small" onClick={onRetry}>
            <Icon name="refresh" className="btn-icon" />
            もう一度読み込む
          </button>
        )}
      </RibatanNote>
    </div>
  );
}

/** 取得の結果に合わせて、読み込み中・未接続・失敗・中身を出しわける */
export function ResourceView<T>({
  resource,
  what,
  children,
  rows,
}: {
  resource: { status: string; reload: () => void } & (
    | { status: "not_connected" }
    | { status: "loading" }
    | { status: "error"; error: unknown }
    | { status: "ready"; data: T; refreshing: boolean; refreshError: unknown | null }
  );
  what: string;
  rows?: number;
  children: (data: T) => ReactNode;
}) {
  if (resource.status === "not_connected") return <NotConnected what={what} />;
  if (resource.status === "loading") return <Loading rows={rows} label={`${what}を読み込み中…`} />;
  if (resource.status === "error") return <ErrorBox error={resource.error} onRetry={resource.reload} />;
  if (resource.status !== "ready") return null;
  return (
    <>
      {resource.refreshError != null && (
        <p className="inline-note inline-note--warn" role="status">
          最新の内容に更新できませんでした。表示は前に読み込んだときのものです。
        </p>
      )}
      {children(resource.data)}
    </>
  );
}

/* ---------------------------------------------------------------- 確認・入力の窓（ネイティブの dialog：フォーカスの閉じこめと Esc は標準のまま） */

export function Dialog({
  open,
  title,
  onClose,
  children,
  footer,
  describedBy,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  describedBy?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const opener = useRef<Element | null>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      opener.current = document.activeElement;
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", "");
    } else if (!open && d.open) {
      if (typeof d.close === "function") d.close();
      else {
        // dialog に対応していない環境：属性を外して、フォーカスを自分で戻す
        d.removeAttribute("open");
        const el = opener.current as HTMLElement | null;
        if (el && typeof el.focus === "function" && document.contains(el)) el.focus();
      }
    }
  }, [open]);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    const onClosed = () => {
      // 閉じたら、開いたボタンへフォーカスを戻す
      const el = opener.current as HTMLElement | null;
      if (el && typeof el.focus === "function" && document.contains(el)) el.focus();
    };
    d.addEventListener("close", onClosed);
    return () => d.removeEventListener("close", onClosed);
  }, []);
  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby={titleId}
      aria-describedby={describedBy}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // 外側（背景）を押したら閉じる
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dialog__panel">
        <div className="dialog__head">
          <h2 id={titleId} className="dialog__title">
            {title}
          </h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="閉じる">
            <Icon name="close" />
          </button>
        </div>
        <div className="dialog__body">{children}</div>
        {footer && <div className="dialog__foot">{footer}</div>}
      </div>
    </dialog>
  );
}

/* ---------------------------------------------------------------- タブ（矢印キーで移る。WAI-ARIA のタブの形） */

export function Tabs<K extends string>({
  label,
  tabs,
  value,
  onChange,
  idBase,
}: {
  label: string;
  tabs: Array<{ id: K; label: string; count?: number }>;
  value: K;
  onChange: (id: K) => void;
  idBase: string;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  // 選んだタブが横にはみ出していたら、見える位置まで寄せる（スマホ）
  useEffect(() => {
    const el = refs.current[value];
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [value]);
  const move = (dir: number) => {
    const i = tabs.findIndex((t) => t.id === value);
    const next = tabs[(i + dir + tabs.length) % tabs.length];
    onChange(next.id);
    refs.current[next.id]?.focus();
  };
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button
          key={t.id}
          ref={(el) => {
            refs.current[t.id] = el;
          }}
          type="button"
          role="tab"
          id={`${idBase}-tab-${t.id}`}
          aria-controls={`${idBase}-panel-${t.id}`}
          aria-selected={t.id === value}
          tabIndex={t.id === value ? 0 : -1}
          className="tabs__tab"
          onClick={() => onChange(t.id)}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") {
              e.preventDefault();
              move(1);
            } else if (e.key === "ArrowLeft") {
              e.preventDefault();
              move(-1);
            } else if (e.key === "Home") {
              e.preventDefault();
              onChange(tabs[0].id);
              refs.current[tabs[0].id]?.focus();
            } else if (e.key === "End") {
              e.preventDefault();
              const last = tabs[tabs.length - 1];
              onChange(last.id);
              refs.current[last.id]?.focus();
            }
          }}
        >
          {t.label}
          {t.count !== undefined && <span className="tabs__count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({ idBase, id, active, children }: { idBase: string; id: string; active: boolean; children: ReactNode }) {
  return (
    <div
      role="tabpanel"
      id={`${idBase}-panel-${id}`}
      aria-labelledby={`${idBase}-tab-${id}`}
      hidden={!active}
      tabIndex={0}
      className="tabs__panel"
    >
      {active && children}
    </div>
  );
}

/* ---------------------------------------------------------------- 文字の入力（ラベル・説明・エラーを結びつける） */

export function Field({
  label,
  hint,
  error,
  children,
  required,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  const describedBy = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={`field${error ? " field--error" : ""}`}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required && <span className="field__req">必須</span>}
      </label>
      {hint && (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      )}
      {children({ id, describedBy, invalid: !!error })}
      {error && (
        <p className="field__error" id={errId}>
          <Icon name="warn" className="field__error-icon" />
          {error}
        </p>
      )}
    </div>
  );
}
